import {CloudDownloadOutlined, SyncOutlined} from "@ant-design/icons";
import {
  Button,
  Col,
  Collapse,
  CollapseProps,
  DatePicker,
  Row,
  Select,
  Space,
  Table,
  Typography,
} from "antd";
import React, { useEffect, useRef } from "react";
import {
  disputeChargebackLogsService,
  vendorCentralCredentialService,
} from "@/services";
import { useObservable } from "@/utils/use-observable";
import dayjs from "dayjs";
import {
  apiDateTimeFormat,
  dateTimeFormat,
  renderDateTime,
  renderStatus,
  tokenSeparator,
} from "@/utils";
import {
  DisputeChargebackStatus,
  disputeChargebackStatusList,
} from "@/models/enum/dispute-chargeback-status";
import "./index.less";
import { TableRef } from "antd/es/table";

const { Title } = Typography;

export default function DisputeChargebackLogsPage() {
  const items: CollapseProps["items"] = [
    {
      key: "filters",
      label: "Filters",
      children: <FilterForm />,
    },
  ];

  const columns = [
    {
      title: "Date",
      key: "date",
      dataIndex: "date",
      width: "150px",
      render: renderDateTime,
    },
    {
      title: "Account Name",
      key: "accountName",
      dataIndex: "accountName",
      width: "250px",
    },
    {
      title: "Status",
      key: "status",
      dataIndex: "status",
      width: "100px",
      render: renderStatus(disputeChargebackStatusList),
    },
    {
      title: "Chargeback ID",
      key: "chargebackId",
      dataIndex: "chargebackId",
      width: "150px",
    },
    {
      title: "Message",
      key: "message",
      dataIndex: "message",
      width: "150px",
    },
  ];

  const isFetching = useObservable(disputeChargebackLogsService.isFetching);
  const logs = useObservable(disputeChargebackLogsService.logs);
  var tableRef = useRef<TableRef>(null);

  const onScroll = (event: any) => {
    if (event.target) {
      let maxScroll = Math.round(
        event.target.scrollHeight - event.target.clientHeight
      );
      let currentScroll = Math.round(event.target.scrollTop);
      if (currentScroll >= maxScroll - 100) {
        disputeChargebackLogsService.next();
      }
    }
  };

  useEffect(() => {
    if (tableRef && tableRef.current) {
      const tbody =
        tableRef.current.nativeElement.querySelector(".ant-table-body");
      if (tbody) {
        tbody.addEventListener("scroll", onScroll);

        return () => {
          tbody.removeEventListener("scroll", onScroll);
        };
      }
    }
  }, []);

  useEffect(() => {
    const subscribe = disputeChargebackLogsService.filters.subscribe(() => {
      tableRef.current?.nativeElement
        .querySelector(".ant-table-body")
        ?.scrollTo({ top: 0 });
      disputeChargebackLogsService.getLogs();
    });

    return () => {
      subscribe.unsubscribe();
    };
  }, []);

  const onRefresh = () => {
    disputeChargebackLogsService.getLogs();
  };

  const onExportCsv = () => {
    disputeChargebackLogsService.exportLogs();
  };

  return (
    <div className="logs-page">
      <Title level={3}>Dispute Chargeback Logs</Title>
      <Row className="actions" justify={"space-between"}>
        <Space>
          <Button icon={<SyncOutlined spin={isFetching} />} onClick={onRefresh}>
            Refresh
          </Button>
          <Button icon={<CloudDownloadOutlined />} onClick={onExportCsv}>
            Export
          </Button>
        </Space>
      </Row>
      <Collapse
        className="filters"
        size="small"
        bordered={false}
        items={items}
      />
      <Table
        ref={tableRef}
        rowClassName={(log) => {
          if (log.status === DisputeChargebackStatus.ERROR) return "table-row-error";
          if (log.status === DisputeChargebackStatus.WARNING) return "table-row-warning";
          return "";
        }}
        columns={columns}
        dataSource={logs}
        rowKey="id"
        size="small"
        pagination={false}
        scroll={{ y: "calc(100vh - 233px)" }}
      />
    </div>
  );
}

function FilterForm() {
  const filters = useObservable(disputeChargebackLogsService.filters);
  const allAccounts = useObservable(vendorCentralCredentialService.allAccounts);

  useEffect(() => {
    vendorCentralCredentialService.getVendorCentralCredentials();
  }, []);

  return (
    <Row gutter={[16, 16]}>
      <Col xl={4} md={6} sm={12} xs={24}>
        <DatePicker
          format={dateTimeFormat()}
          showTime
          value={
            filters.dateFrom ? dayjs(filters.dateFrom, apiDateTimeFormat) : null
          }
          onChange={disputeChargebackLogsService.setDateFrom}
          placeholder="Date from"
          style={{ width: "100%" }}
          allowClear
        />
      </Col>
      <Col xl={4} md={6} sm={12} xs={24}>
        <DatePicker
          format={dateTimeFormat()}
          showTime
          value={
            filters.dateTo ? dayjs(filters.dateTo, apiDateTimeFormat) : null
          }
          onChange={disputeChargebackLogsService.setDateTo}
          placeholder="Date to"
          style={{ width: "100%" }}
          allowClear
        />
      </Col>
      <Col xl={4} md={6} sm={12} xs={24}>
        <Select
          options={allAccounts}
          optionFilterProp="label"
          value={filters.accountNames}
          onChange={disputeChargebackLogsService.setAccountNames}
          placeholder="Account Names"
          allowClear
          style={{ width: "100%" }}
          mode="multiple"
        />
      </Col>
      <Col xl={4} md={6} sm={12} xs={24}>
        <Select
          options={disputeChargebackStatusList}
          value={filters.statuses}
          onChange={disputeChargebackLogsService.setStatuses}
          allowClear
          placeholder="Statuses"
          style={{ width: "100%" }}
          mode="multiple"
        />
      </Col>
      <Col xl={4} md={6} sm={12} xs={24}>
        <Select
          value={filters.chargebackIds}
          onChange={disputeChargebackLogsService.setChargebackIds}
          allowClear
          placeholder="Chargeback IDs"
          style={{ width: "100%" }}
          mode="tags"
          tokenSeparators={tokenSeparator}
          open={false}
          suffixIcon={false}
        />
      </Col>
    </Row>
  );
}

